import React, { useState, useEffect } from 'react';
import { Button, Modal, Alert } from 'antd';
import {
  ThunderboltFilled,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  CheckOutlined,
  RightOutlined,
  RollbackOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { getConfig } from './mockApi';
import useAiCrew from './useAiCrew';
import PromptPanel from './PromptPanel';
import ProposalHeader from './ProposalHeader';
import ClarifyChips from './ClarifyChips';
import ClarifyConversation from './ClarifyConversation';
import EmployeeList from './EmployeeList';
import ExecuteBar from './ExecuteBar';
import IntentActionReview from './IntentActionReview';
import ResultView from './ResultView';
import AnswerView from './AnswerView';
import ChatComposer from './ChatComposer';
import { INTENT_TYPE } from './contracts';
import './aiCrew.css';

/**
 * AiCrewEntry - The entry button and container modal for the CrewCall Agent.
 * Co-exists with the traditional Create Crew wizard without modifying its behavior.
 */
export default function AiCrewEntry({ disabled = false, selectedDate = null, licensed = false, onCrewCreated, onFallbackToManualWizard }) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [manualOverride, setManualOverride] = useState(false);

  const aiCrewState = useAiCrew();

  useEffect(() => {
    let mounted = true;
    getConfig()
      .then((cfg) => {
        if (mounted && cfg) {
          setConfig(cfg);
        }
      })
      .catch((err) => {
        console.warn('[AiCrewEntry] Failed to fetch config:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleOpen = () => {
    // Config-level LLM outage: skip the AI modal entirely and hand off to the existing
    // manual crew wizard rather than showing a second, AI-modal-shaped manual form.
    if (config && config.llmAvailable === false) {
      if (typeof onFallbackToManualWizard === 'function') {
        onFallbackToManualWizard();
      }
      return;
    }
    if (config?.ukg?.authMode === 'SERVICE_ACCOUNT' && !config.ukg.connected) {
      Modal.error({
        title: 'UKG UAT is unavailable',
        content: 'The CrewCall UKG service account is missing configuration or could not be initialized.',
      });
      return;
    }
    if (config?.ukg?.requiresReauthentication) {
      Modal.confirm({
        title: 'Connect UKG to schedule crews',
        content: 'CrewCall uses your UKG access to load authorized employees and create shifts.',
        okText: 'Reconnect UKG',
        cancelText: 'Cancel',
        okButtonProps: { disabled: !config.ukg.authorizationUrl },
        onOk: () => {
          if (config.ukg.authorizationUrl) window.location.assign(config.ukg.authorizationUrl);
        },
      });
      return;
    }
    aiCrewState.reset();
    setManualOverride(false);
    setOpen(true);
  };

  const notifyCrewCreated = () => {
    const lastResult = aiCrewState.result;
    const isSuccess = lastResult && (lastResult.status === 'SUCCESS' || lastResult.status === 'PARTIAL' || lastResult.crewId);
    const createdCrew = isSuccess ? {
      crewId: lastResult?.crewId || null,
      date: lastResult?.date || aiCrewState.proposal?.crew?.date || null,
    } : null;

    if (createdCrew && typeof onCrewCreated === 'function') {
      onCrewCreated(createdCrew);
    }
  };

  const handleClose = () => {
    notifyCrewCreated();
    aiCrewState.reset();
    setManualOverride(false);
    setOpen(false);
  };

  const handleRequestClose = () => {
    // Executing writes to VYN/UKG. Keep the result attached to this flow until it
    // completes so closing cannot hide a successful write or allow another run.
    if (aiCrewState.step === 'executing') return;
    if (aiCrewState.step === 'proposal' && aiCrewState.proposal) {
      Modal.confirm({
        title: 'Discard crew proposal?',
        content: 'Closing now discards this proposal and your selections.',
        okText: 'Discard & Close',
        okType: 'danger',
        cancelText: 'Keep Editing',
        onOk: () => {
          handleClose();
        },
      });
    } else {
      handleClose();
    }
  };

  // U10: Keyboard Escape closes with confirm if proposal loaded
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleRequestClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, aiCrewState.step, aiCrewState.proposal, aiCrewState.result, onCrewCreated]);

  const isLlmAvailable = manualOverride ? false : (config ? config.llmAvailable !== false : true);
  const hasClarifications = Boolean(
    aiCrewState.proposal?.clarifications && aiCrewState.proposal.clarifications.length > 0
  );

  // If AI Crew is disabled in tenant config, or the org's license doesn't include
  // 'crewcall', hide the button completely. Checked after every hook has run (not an
  // early return) so the hook order never changes between renders.
  if (!licensed || (config && config.aiCrewEnabled === false)) {
    return null;
  }

  // Stepper state determination
  const isPromptStage = aiCrewState.step === 'prompt' || aiCrewState.step === 'analyzing';
  const isClarifyingStage = aiCrewState.step === 'clarifying';
  const isReviewStage = (aiCrewState.step === 'proposal' || aiCrewState.step === 'executing') && Boolean(aiCrewState.proposal);
  const isResultStage = aiCrewState.step === 'result';
  const isAnswerStage = aiCrewState.step === 'answer' && Boolean(aiCrewState.answer);

  return (
    <>
      <Button
        type="default"
        icon={<ThunderboltFilled style={{ color: '#1677ff' }} />}
        onClick={handleOpen}
        disabled={disabled}
        style={{
          borderColor: '#91d5ff',
          color: '#1677ff',
          fontWeight: 600,
          marginLeft: 8,
          boxShadow: '0 1px 3px rgba(22, 119, 255, 0.1)',
        }}
      >
        Create with AI
      </Button>

      <Modal
        open={open}
        onCancel={handleRequestClose}
        footer={null}
        width="82%"
        centered={false}
        maskClosable={false}
        keyboard={false}
        closeIcon={null}
        style={{ top: 8, maxWidth: 1020, paddingBottom: 0 }}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <div className="ai-crew-modal-container" id="ai-crew-content" data-testid="ai-crew-container">
          {/* Top header bar */}
          <div className="ai-crew-modal-header">
            {/* Brand Left */}
            <div className="ai-crew-header-brand">
              {((isReviewStage || isClarifyingStage) ||
                (isResultStage &&
                  (aiCrewState.result?.status === 'CONFLICT' || aiCrewState.result?.status === 'FAILED'))) && (
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  disabled={aiCrewState.step === 'executing'}
                  onClick={() => {
                    if (aiCrewState.step === 'result') {
                      aiCrewState.setStep('proposal');
                    } else if (aiCrewState.step === 'proposal' && aiCrewState.conversation?.length > 0) {
                      aiCrewState.setStep('clarifying');
                    } else {
                      aiCrewState.setStep('prompt');
                    }
                  }}
                  title={aiCrewState.step === 'result' ? 'Back to proposal' : 'Back to conversation'}
                />
              )}
              <div className="ai-crew-brand-icon">
                <ThunderboltFilled />
              </div>
              <div>
                <h2 className="ai-crew-brand-title">
                  CrewCall Agent
                  <span className="ai-crew-brand-badge">
                    {isLlmAvailable ? 'AI-Assisted' : 'Manual Mode'}
                  </span>
                </h2>
              </div>
            </div>

            {/* Centered Workflow Stepper */}
            <div className="ai-crew-header-stepper">
              <span className={`ai-crew-stepper-step ${(isPromptStage || isClarifyingStage || isAnswerStage) ? 'active' : 'completed'}`}>
                {!(isPromptStage || isClarifyingStage || isAnswerStage) && <CheckOutlined style={{ fontSize: 10 }} />}
                1. Intent
              </span>
              <RightOutlined className="ai-crew-stepper-arrow" />
              <span className={`ai-crew-stepper-step ${isReviewStage ? 'active' : isResultStage ? 'completed' : ''}`}>
                {isResultStage && <CheckOutlined style={{ fontSize: 10 }} />}
                2. Review
              </span>
              <RightOutlined className="ai-crew-stepper-arrow" />
              <span className={`ai-crew-stepper-step ${isResultStage ? 'active' : ''}`}>
                3. Scheduled
              </span>
            </div>

            {/* Header Right Actions */}
            <div className="ai-crew-header-actions">
              {(isPromptStage || isClarifyingStage) && aiCrewState.proposal && (
                <>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowRightOutlined />}
                    onClick={() => aiCrewState.setStep('proposal')}
                    className="ai-crew-header-subtle-btn"
                  >
                    View Proposal
                  </Button>
                  <span className="ai-crew-header-divider" />
                </>
              )}
              {isClarifyingStage && (
                <>
                  <Button
                    type="text"
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={aiCrewState.reset}
                    className="ai-crew-header-subtle-btn"
                  >
                    Start Over
                  </Button>
                  <span className="ai-crew-header-divider" />
                </>
              )}
              <span className="ai-crew-esc-hint">Esc</span>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleRequestClose}
                disabled={aiCrewState.step === 'executing'}
                aria-label="Close"
              />
            </div>
          </div>

          <div className={`ai-crew-modal-scroll-body ${isClarifyingStage ? 'ai-crew-modal-scroll-body--chat' : ''}`}>
            {/* Error banner if any */}
            {aiCrewState.error && (
              <Alert
                message={aiCrewState.error}
                type="error"
                showIcon
                closable
                action={aiCrewState.errorCode === 'UKG_REAUTH_REQUIRED' && (
                  <Button size="small" type="primary" onClick={() => {
                    const url = aiCrewState.config?.ukg?.authorizationUrl || config?.ukg?.authorizationUrl;
                    if (url) window.location.assign(url);
                  }}>
                    Reconnect UKG
                  </Button>
                )}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Step 1: Initial Prompt Panel */}
            {isPromptStage && (
              <PromptPanel
                onFallbackToManual={onFallbackToManualWizard}
                timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                step={aiCrewState.step}
                prompt={aiCrewState.prompt}
                setPrompt={aiCrewState.setPrompt}
                onBuildCrew={aiCrewState.runAnalyze}
                llmAvailable={isLlmAvailable}
                events={aiCrewState.config?.events || []}
                nearestUpcomingEvent={aiCrewState.config?.nearestUpcomingEvent || null}
                promptSuggestions={aiCrewState.config?.promptSuggestions || []}
              />
            )}

            {/* Step 1.5: Conversational Clarification Dialogue */}
            {isClarifyingStage && (
              <ClarifyConversation
                currentCrew={aiCrewState.proposal?.crew}
                conversation={aiCrewState.conversation}
                onClarify={aiCrewState.runClarify}
                isClarifying={aiCrewState.isClarifying}
                onFallbackToManual={onFallbackToManualWizard}
                onRestart={aiCrewState.reset}
              />
            )}

            {/* Step 2: Proposal loaded or executing */}
            {isReviewStage && aiCrewState.proposal && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Status: NO_MATCH banner */}
                {aiCrewState.proposal.status === 'NO_MATCH' && (
                  <Alert
                    message={aiCrewState.proposal?.crew
                      ? 'Candidate List Unavailable'
                      : 'No Matching Event Found'}
                    description={aiCrewState.proposal.message || 'I could not match that to an event you have access to.'}
                    type="warning"
                    showIcon
                    action={
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => {
                          setManualOverride(true);
                          aiCrewState.setStep('prompt');
                        }}
                      >
                        Use Manual Mode
                      </Button>
                    }
                  />
                )}

                {/* TRANSFER_OWNER / COPY_CREW: minimal confirm card, no crew/candidates shape */}
                {aiCrewState.proposal.intentType && aiCrewState.proposal.intentType !== INTENT_TYPE.CREATE_CREW && (
                  <IntentActionReview
                    proposal={aiCrewState.proposal}
                    isExecuting={aiCrewState.step === 'executing'}
                    onExecute={aiCrewState.runExecute}
                    onCancel={aiCrewState.reset}
                  />
                )}
                {aiCrewState.proposal.intentType && aiCrewState.proposal.intentType !== INTENT_TYPE.CREATE_CREW
                  && aiCrewState.step === 'proposal' && (
                  <ChatComposer onAsk={aiCrewState.runAnalyze} placeholder="Or ask something else, e.g. show someone's schedule" />
                )}

                {/* Proposal Header (when crew details resolved or partially resolved) */}
                {aiCrewState.proposal.crew && (
                  <ProposalHeader
                    proposal={aiCrewState.proposal}
                    originalPrompt={aiCrewState.prompt}
                    crewLeadId={aiCrewState.crewLeadId}
                    onCrewLeadChange={aiCrewState.setCrewLeadId}
                  />
                )}

                {/* Candidate Employee List & Selection (U7) */}
                {aiCrewState.proposal.candidates && aiCrewState.proposal.candidates.length > 0 && (
                  <EmployeeList
                    candidates={aiCrewState.proposal.candidates}
                    selectedIds={aiCrewState.selectedIds}
                    onToggle={aiCrewState.toggleEmployee}
                    headcount={aiCrewState.proposal.crew?.headcount || 0}
                    backfillNote={aiCrewState.backfillNote}
                  />
                )}

                {/* ExecuteBar pinned at bottom (U9) */}
                {aiCrewState.proposal.crew && (
                  <ExecuteBar
                    selectedCount={aiCrewState.selectedIds?.size || 0}
                    headcount={aiCrewState.proposal.crew?.headcount || 0}
                    isExecuting={aiCrewState.step === 'executing'}
                    onExecute={aiCrewState.runExecute}
                  />
                )}
              </div>
            )}

            {/* Read-only reply: an employee's schedule, who is on a crew */}
            {isAnswerStage && (
              <AnswerView
                conversation={aiCrewState.conversation}
                answer={aiCrewState.answer}
                question={aiCrewState.prompt}
                onAsk={aiCrewState.runAnalyze}
              />
            )}

            {/* Step 5: Result screen (U9) */}
            {isResultStage && aiCrewState.result && (
              <ResultView
                result={aiCrewState.result}
                proposal={aiCrewState.proposal}
                selectedCount={aiCrewState.selectedIds?.size || 0}
                onViewCrew={() => {
                  handleClose();
                }}
                onCreateAnother={() => {
                  notifyCrewCreated();
                  aiCrewState.reset();
                  setManualOverride(false);
                }}
                onRetryExecute={aiCrewState.runExecute}
                onBackToProposal={() => {
                  aiCrewState.setStep('proposal');
                }}
                isRetrying={aiCrewState.step === 'executing'}
                onReconnectUkg={() => {
                  const url = aiCrewState.config?.ukg?.authorizationUrl || config?.ukg?.authorizationUrl;
                  if (url) window.location.assign(url);
                }}
              />
            )}
            {isResultStage && aiCrewState.result && aiCrewState.proposal?.intentType !== INTENT_TYPE.CREATE_CREW && (
              <ChatComposer onAsk={(...args) => {
                notifyCrewCreated();
                return aiCrewState.runAnalyze(...args);
              }} />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
